import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { useGetGymBranding, useGetBillingStatus } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import { Palette, Save, Image, Lock, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { ImageUpload } from "@/components/image-upload";
import { Link } from "wouter";
import { cn } from "@/lib/utils";

function isValidHex(value: string): boolean {
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);
}

export default function AdminBranding() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: branding, refetch } = useGetGymBranding(user?.gymId || "", { query: { enabled: !!user?.gymId, retry: false } });
  const { data: billing, isLoading: billingLoading } = useGetBillingStatus(user?.gymId || "", { query: { enabled: !!user?.gymId, retry: false } });

  const isPremium = billingLoading ? true : billing?.platformPlan === "PREMIUM";

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    brandingEnabled: true,
    brandPrimaryColor: "#e8c848",
    brandAccentColor: "#1a1a2e",
    brandLogoUrl: "",
    brandFaviconUrl: "",
    brandAppName: "",
    customDomain: "",
  });

  const [primaryColorInput, setPrimaryColorInput] = useState(form.brandPrimaryColor);
  const [accentColorInput, setAccentColorInput] = useState(form.brandAccentColor);
  const [primaryColorError, setPrimaryColorError] = useState(false);
  const [accentColorError, setAccentColorError] = useState(false);

  useEffect(() => {
    if (branding) {
      const primary = branding.brandPrimaryColor || "#e8c848";
      const accent = branding.brandAccentColor || "#1a1a2e";
      setForm({
        brandingEnabled: branding.brandingEnabled ?? true,
        brandPrimaryColor: primary,
        brandAccentColor: accent,
        brandLogoUrl: branding.brandLogoUrl || "",
        brandFaviconUrl: branding.brandFaviconUrl || "",
        brandAppName: branding.brandAppName || "",
        customDomain: branding.customDomain || "",
      });
      setPrimaryColorInput(primary);
      setAccentColorInput(accent);
    }
  }, [branding]);

  async function handleSave() {
    if (!user?.gymId) return;
    setSaving(true);
    try {
      await apiFetch(`/gyms/${user.gymId}/branding`, { method: "PUT", body: JSON.stringify(form) });
      toast({ title: "Branding updated" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const handlePrimaryTextChange = (value: string) => {
    setPrimaryColorInput(value);
    if (isValidHex(value)) {
      setPrimaryColorError(false);
      setForm({ ...form, brandPrimaryColor: value });
    } else {
      setPrimaryColorError(true);
    }
  };

  const handleAccentTextChange = (value: string) => {
    setAccentColorInput(value);
    if (isValidHex(value)) {
      setAccentColorError(false);
      setForm({ ...form, brandAccentColor: value });
    } else {
      setAccentColorError(true);
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-2xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Branding</h1>
          <p className="text-muted-foreground">Customize your gym's appearance (Premium feature)</p>
        </div>

        {!isPremium && (
          <Card className="glass-panel border-primary/30 bg-primary/5">
            <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <Lock className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-display font-bold text-white mb-2">Premium Feature</h2>
                <p className="text-muted-foreground text-sm max-w-sm">
                  Custom branding is available on the Premium plan. Upgrade to customize your gym's colors, logo, app name, and domain.
                </p>
              </div>
              <Link href="/admin/billing">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2">
                  <Star className="w-4 h-4" /> Upgrade to Premium
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        <div className={cn(!isPremium && "opacity-50 pointer-events-none select-none")}>
          <div className="flex flex-col gap-8">
            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-xl font-display text-white flex items-center gap-2"><Palette className="w-5 h-5" /> Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white block">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={form.brandPrimaryColor}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({ ...form, brandPrimaryColor: val });
                          setPrimaryColorInput(val);
                          setPrimaryColorError(false);
                        }}
                        className="w-10 h-10 rounded cursor-pointer border-0 flex-shrink-0"
                        aria-label="Primary color picker"
                      />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={primaryColorInput}
                          onChange={(e) => handlePrimaryTextChange(e.target.value)}
                          className={cn("bg-background border-white/10 font-mono", primaryColorError && "border-destructive")}
                          placeholder="#e8c848"
                          aria-label="Primary color hex value"
                        />
                        {primaryColorError && (
                          <p className="text-xs text-destructive mt-1">Enter a valid hex color (e.g. #e8c848)</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white block">Accent Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={form.brandAccentColor}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({ ...form, brandAccentColor: val });
                          setAccentColorInput(val);
                          setAccentColorError(false);
                        }}
                        className="w-10 h-10 rounded cursor-pointer border-0 flex-shrink-0"
                        aria-label="Accent color picker"
                      />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={accentColorInput}
                          onChange={(e) => handleAccentTextChange(e.target.value)}
                          className={cn("bg-background border-white/10 font-mono", accentColorError && "border-destructive")}
                          placeholder="#1a1a2e"
                          aria-label="Accent color hex value"
                        />
                        {accentColorError && (
                          <p className="text-xs text-destructive mt-1">Enter a valid hex color (e.g. #1a1a2e)</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl" style={{ background: form.brandAccentColor }}>
                  <p className="text-lg font-display font-bold" style={{ color: form.brandPrimaryColor }}>{form.brandAppName || "Your Gym Name"}</p>
                  <p className="text-sm mt-1" style={{ color: `${form.brandPrimaryColor}99` }}>Book Your Favorite Instructor Now</p>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-panel border-white/5">
              <CardHeader>
                <CardTitle className="text-xl font-display text-white flex items-center gap-2"><Image className="w-5 h-5" /> Logo & Assets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">App Name</label>
                  <Input value={form.brandAppName} onChange={(e) => setForm({ ...form, brandAppName: e.target.value })} placeholder="Your Gym Name" className="bg-background border-white/10" />
                </div>
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Logo</label>
                  <div className="space-y-3">
                    <ImageUpload
                      currentUrl={form.brandLogoUrl}
                      onUpload={(url) => { setForm({ ...form, brandLogoUrl: url }); refetch(); }}
                      uploadEndpoint="/uploads/gym-logo"
                      label="Upload Logo"
                      shape="square"
                      size="lg"
                    />
                    <Input value={form.brandLogoUrl} onChange={(e) => setForm({ ...form, brandLogoUrl: e.target.value })} placeholder="Or paste a URL..." className="bg-background border-white/10 text-xs" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Favicon</label>
                  <div className="space-y-3">
                    <ImageUpload
                      currentUrl={form.brandFaviconUrl}
                      onUpload={(url) => setForm({ ...form, brandFaviconUrl: url })}
                      uploadEndpoint="/uploads/gym-favicon"
                      label="Upload Favicon"
                      shape="square"
                      size="sm"
                    />
                    <Input value={form.brandFaviconUrl} onChange={(e) => setForm({ ...form, brandFaviconUrl: e.target.value })} placeholder="Or paste a URL..." className="bg-background border-white/10 text-xs" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Custom Domain</label>
                  <Input value={form.customDomain} onChange={(e) => setForm({ ...form, customDomain: e.target.value })} placeholder="training.yourgym.com" className="bg-background border-white/10" />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleSave} disabled={saving || !isPremium} className="bg-primary text-primary-foreground hover:bg-primary/80">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Branding"}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
